// Récupération d'un compte : mot de passe oublié.
//
// C'est l'écran le plus délicat du produit, parce qu'il travaille **sans
// session** : l'avocat ne peut pas s'authentifier, il ne possède plus rien. Le
// serveur ne peut donc rien vérifier de lui, et ne prétend pas le faire.
//
// Ce sont ses **dépositaires** qui l'authentifient, hors bande : il les appelle,
// et ils comparent l'empreinte affichée ici avec celle qu'ils voient chez eux.
// L'interface les met tous les deux face à cette responsabilité, sans l'enrober.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  clearPendingRecovery,
  completeRecovery,
  loadPendingRecovery,
  openRecovery,
  pendingFingerprint,
  recoveryStatus,
  unsealReleases,
  type PendingRecovery,
  type RecoveryStatus,
} from '../crypto/recovery-flow';
import { tenantFromHost } from './tenant';

const TENANT_FROM_HOST = tenantFromHost();
const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

const field =
  'mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm ' +
  'focus:border-blue-600 focus:outline-none';
const button =
  'mt-6 w-full rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white ' +
  'hover:bg-blue-800 disabled:opacity-50';

export function RecoverAccount({ onDone }: { onDone: () => void }) {
  // La demande survit au rechargement de la page : les dépositaires mettront
  // sans doute des heures à répondre — le temps de se téléphoner.
  const [pending, setPending] = useState<PendingRecovery | null>(loadPendingRecovery);
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [status, setStatus] = useState<RecoveryStatus | null>(null);

  const [tenantSlug, setTenantSlug] = useState(TENANT_FROM_HOST ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [twoFactorLeve, setTwoFactorLeve] = useState(false);

  // Après un rechargement de page, l'empreinte se recalcule depuis la clé
  // conservée : c'est la même que celle déjà épelée aux dépositaires.
  useEffect(() => {
    if (!pending || fingerprint) return;
    void pendingFingerprint(pending).then(setFingerprint);
  }, [pending, fingerprint]);

  const refresh = useCallback(async () => {
    if (!pending) return;
    try {
      setStatus(await recoveryStatus(pending));
    } catch (e) {
      setError(message(e));
    }
  }, [pending]);

  useEffect(() => {
    if (!pending || done) return;
    void refresh();
    const id = setInterval(() => void refresh(), 6000);
    return () => clearInterval(id);
  }, [pending, done, refresh]);

  async function onOpen(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const opened = await openRecovery(tenantSlug.trim() || null, email.trim());
      setPending(opened.pending);
      setFingerprint(opened.fingerprint);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function onReset(event: FormEvent) {
    event.preventDefault();
    if (!pending || !status) return;
    setBusy(true);
    setError(null);
    try {
      const parts = await unsealReleases(pending, status);
      const outcome = await completeRecovery(pending, status, parts, password);
      setTwoFactorLeve(outcome.twoFactorDisabled);
      setDone(true);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  function abandonner() {
    clearPendingRecovery();
    setPending(null);
    setStatus(null);
    setFingerprint(null);
    onDone();
  }

  // --- Terminé ---
  if (done) {
    return (
      <section className="mx-auto mt-10 max-w-sm text-left" data-testid="recovery-done">
        <h2 className="text-lg font-semibold text-emerald-700">Accès rétabli</h2>
        <p className="mt-2 text-sm text-slate-600">
          Ton nouveau mot de passe est en place. Ta clé maîtresse, elle,{' '}
          <strong>n&apos;a pas changé</strong> — tes dossiers, tes documents et les
          parts déjà déposées chez tes confrères restent valides.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Toutes tes sessions ont été fermées. Reconnecte-toi.
        </p>

        {twoFactorLeve && (
          <p
            data-testid="two-factor-lifted"
            className="mt-3 rounded bg-amber-50 p-2 text-xs text-amber-800"
          >
            <strong>Ton second facteur a été levé.</strong> Il fallait bien : qui
            perd son mot de passe peut avoir perdu son téléphone dans le même
            sinistre, et tu serais resté enfermé dehors malgré cette récupération.{' '}
            <strong>Remets-le en place dès ta reconnexion</strong> — sans lui, ton
            mot de passe est la seule porte.
          </p>
        )}

        <button type="button" onClick={onDone} className={button} data-testid="recovery-back">
          Aller à la connexion
        </button>
      </section>
    );
  }

  // --- Demande en cours ---
  if (pending) {
    const releases = status?.releases.length ?? 0;
    const threshold = status?.threshold ?? 2;
    const assez = releases >= threshold;

    return (
      <section
        className="mx-auto mt-10 max-w-sm text-left"
        data-testid="recovery-pending"
      >
        <h2 className="text-lg font-semibold text-slate-900">
          Demande de récupération en cours
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Compte : <span className="font-mono text-xs">{pending.email}</span>
        </p>

        <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900">
            Appelle tes deux dépositaires et épelle-leur cette empreinte :
          </p>
          <p
            data-testid="requester-fingerprint"
            className="mt-2 select-all break-all font-mono text-sm text-slate-900"
          >
            {fingerprint ?? '…'}
          </p>
          <p className="mt-2 text-[11px] text-amber-800">
            Le serveur ne peut pas savoir que c&apos;est bien toi : il ne voit
            qu&apos;une adresse et une clé. <strong>Ce sont eux qui te
            reconnaissent</strong>, en vérifiant que cette empreinte est exactement
            celle qui s&apos;affiche sur leur écran.
          </p>
        </div>

        <p
          className="mt-4 text-sm text-slate-600"
          data-testid="recovery-progress"
        >
          {releases} part{releases > 1 ? 's' : ''} libérée{releases > 1 ? 's' : ''} sur{' '}
          {threshold} nécessaires.
        </p>

        {assez ? (
          <form onSubmit={onReset}>
            <p className="mt-4 text-sm text-emerald-700">
              Les parts suffisent. Choisis ton nouveau mot de passe.
            </p>
            <label className="mt-4 block text-sm font-medium text-slate-700">
              Nouveau mot de passe
              <input
                className={field}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="new-password"
              />
            </label>
            {error && (
              <p data-testid="recovery-error" className="mt-4 text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy || password.length < 8}
              className={button}
              data-testid="submit-recovery"
            >
              {busy ? 'Reconstitution…' : 'Reconstituer ma clé et rétablir l’accès'}
            </button>
          </form>
        ) : (
          <>
            <p className="mt-2 text-xs text-slate-500">
              Cette page peut être fermée : la demande reste ouverte, et sa clé est
              conservée sur cet appareil. Reviens ici depuis{' '}
              <strong>ce navigateur</strong> — c&apos;est le seul à pouvoir
              descelller les parts.
            </p>
            {error && (
              <p data-testid="recovery-error" className="mt-4 text-sm text-red-600">
                {error}
              </p>
            )}
          </>
        )}

        <button
          type="button"
          onClick={abandonner}
          className="mt-4 w-full text-xs text-slate-500 underline hover:text-slate-700"
          data-testid="abandon-recovery"
        >
          Abandonner cette demande
        </button>
      </section>
    );
  }

  // --- Ouverture ---
  return (
    <form onSubmit={onOpen} className="mx-auto mt-10 max-w-sm text-left">
      <h2 className="text-lg font-semibold text-slate-900">Mot de passe oublié</h2>
      <p className="mt-1 text-sm text-slate-500">
        Le serveur ne peut pas te rendre ton compte : il ne détient pas ta clé.
        Mais <strong>tes dépositaires</strong> le peuvent — deux d&apos;entre eux
        suffisent.
      </p>

      {!TENANT_FROM_HOST && (
        <label className="mt-6 block text-sm font-medium text-slate-700">
          Cabinet
          <input
            className={field}
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            placeholder="ex. cabinet-dupont"
            data-testid="recovery-tenant"
          />
        </label>
      )}

      <label className="mt-4 block text-sm font-medium text-slate-700">
        Adresse e-mail
        <input
          className={field}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          data-testid="recovery-email"
        />
      </label>

      {error && (
        <p data-testid="recovery-error" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <button type="submit" disabled={busy} className={button} data-testid="open-recovery">
        {busy ? 'Ouverture…' : 'Demander une récupération'}
      </button>

      <button
        type="button"
        onClick={onDone}
        className="mt-4 w-full text-xs text-slate-500 underline hover:text-slate-700"
      >
        Revenir à la connexion
      </button>
    </form>
  );
}
